import { computed, onMounted, onUnmounted } from 'vue';
import { useNavigationGuardStore } from '../store/navigation-guard.store';
import { onBeforeRouteLeave } from 'vue-router';
import { useModal } from '@tundr/modal';
import { useLocale } from '@tundr/i18n';

const LOGIN_ROUTE_NAME = 'auth.login';

type UseNavigationGuardProps = {
  confirmModalLabels?: {
    title?: string;
    description?: string;
  };
};

/**
 * This composable is used to enable the navigation guard at the start of a flow with steps
 */

export const useNavigationGuard = (props: UseNavigationGuardProps = {}) => {
  const navigationGuardStore = useNavigationGuardStore();
  const { openConfirmModal } = useModal();
  const { t } = useLocale('common');

  const isGuardEnabled = computed(() => navigationGuardStore.isBlocked);
  const disableGuard = () => navigationGuardStore.setIsBlocked(false);

  onMounted(() => {
    if (props.confirmModalLabels) {
      navigationGuardStore.setConfirmModalLabels(props.confirmModalLabels);
    }
    navigationGuardStore.setIsBlocked(true);
  });

  onUnmounted(() => {
    disableGuard();
    navigationGuardStore.setIsExitConfirmed(false);
  });

  onBeforeRouteLeave((to, _, next) => {
    if (
      to.name !== LOGIN_ROUTE_NAME &&
      isGuardEnabled.value &&
      !navigationGuardStore.isExitConfirmed
    ) {
      openConfirmModal({
        title:
          navigationGuardStore.confirmModalLabels?.title ||
          t('confirm_exit.title'),
        message:
          navigationGuardStore.confirmModalLabels?.description ||
          t('confirm_exit.description'),
        config: { blocking: true },
        labels: {
          confirm: t('common:actions.exit'),
          cancel: t('common:actions.cancel'),
        },
        events: {
          onConfirm: () => {
            navigationGuardStore.setIsBlocked(false);
            next(true);
          },
          onCancel: () => {
            next(false);
          },
        },
      });
    } else {
      next(true);
    }
  });

  return {
    disableGuard,
    isGuardEnabled,
  };
};
